import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faComment, faComments, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService } from 'src/app/services/http/users.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { TChatStateService } from 'src/app/services/state/tchat-state.service';
import { TchatRoom } from '../../models/server/tchat-room';
import { TchatUser } from '../../models/server/tchat-user';
import { TchatRoomComponent } from './tchat-room/tchat-room.component';
import { TchatRoomsComponent } from './tchat-rooms/tchat-rooms.component';
import { TchatUsersComponent } from './tchat-users/tchat-users.component';

/**
 * Type de menu tchat.
 */
export enum MenuType {
    users = 1,
    rooms = 2,
}

@Component({
    selector: 'app-tchat',
    templateUrl: './tchat.component.html',
    styleUrls: ['./tchat.component.scss'],
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatSidenavModule,
        FaIconComponent,
        MatTooltipModule,
        NgClass,
        TchatUsersComponent,
        TchatRoomsComponent,
        TchatRoomComponent,
    ]
})
export class TchatComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    usersIcon = faUsers;
    ChatRoomsIcon = faComments;
    NewChatRoomIcon = faComment;

    /**
     * Références des Enums utilisées dans le template.
     */
    public MenuType = MenuType;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Menu actuellement selectionné.
     */
    public selectedMenu: MenuType = MenuType.users;

    /**
     * Conversation courante.
     */
    public currentRoom: TchatRoom;

    /**
     * Constructeur.
     * @param tchatService
     * @param connectedUserStateService
     */
    constructor(
        public tchatService: TChatStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private usersService: UsersService
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.tchatService.roomSelectedChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((room) => {
            this.currentRoom = room;
        });
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur selectionne un utilisateur.
     * @param userSelected : Utilisateur selectionné.
     */
    public onUserSelected(userSelected: TchatUser): void {
        // TODO : Ne pas faire l'appel à chaque fois mais garder les infos de l'utilisateur en mémoire à la connexion.
        this.usersService.get(this.connectedUserStateService.getUserConnected().userId).subscribe((x) => {
            let currentTchatUser = new TchatUser(x.account, x.displayName, true, null);

            this.currentRoom = this.tchatService.room([currentTchatUser, userSelected]);
            this.tchatService.roomSelectedChange(this.currentRoom);
        });
    }

    /**
     * Appelée lorsque l'utilisateur selectionne une conversation.
     * @param room : Conversation selectionnée.
     */
    public onRoomSelected(room: TchatRoom): void {
        this.currentRoom = room;
        this.tchatService.roomSelectedChange(this.currentRoom);
    }

    /**
     * Crée une nouvelle conversation.
     */
    public createRoom(): void {
        this.selectedMenu = MenuType.rooms;

        // TODO : Ne pas faire l'appel à chaque fois mais garder les infos de l'utilisateur en mémoire à la connexion.
        this.usersService.get(this.connectedUserStateService.getUserConnected().userId).subscribe((x) => {
            let currentTchatUser = new TchatUser(x.account, x.displayName, true, null);

            this.currentRoom = this.tchatService.room([currentTchatUser]);
            this.tchatService.roomSelectedChange(this.currentRoom);
        });
    }
}
